import { Optional, Resume, StepByStepFlowStepId } from 'packages/types'
import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { DeleteSectionFunc, RenameSectionFunc, SectionData } from 'builder/components/Section'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  selectors as editorSelectors,
  actions as editorActions,
  ResumeSectionId,
  AddCustomSectionPayload,
  MoveSectionPayload,
  UpdateSimpleFieldFunc,
  SectionNames,
} from 'builder/modules/resumeEditor'
import {
  AddCardFunc,
  CardOperationFunc,
  MoveCardFunc,
  UpdateCardFunc,
} from 'builder/components/Card'
import { GeneralEditorLevels } from 'builder/modules/generalEditor'
import { constructSectionsData, Section } from 'builder/components/SectionsList'
import ProfileSection from 'builder/components/ProfileSummary/ProfileSection'
import PersonalDetailsSection from 'builder/components/PersonalDetailsSection'
import { ATS_TEMPLATES, TemplateName } from 'builder/components/Helper/constants'
import { AdditionalSections } from 'builder/components/AdditionalSections'
import { displaySectionPerTemplateConfig } from 'builder/components/DocumentEditor/ATSResumeEditor/builderSectionsConfig'
import { generateRandomId } from 'builder/utils/generateRandomId'
import { useI18n } from 'builder/hooks/useI18n'
import PowerStatementSection from 'builder/components/PowerStatement/PowerStatementSection'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import ATSPersonalDetailsSection from '../../../ATSResumeEditor/PersonalDetailsSection'
import { STEP_BY_STEP_FLOW_VISITED_SECTIONS_LOCAL_STORAGE_KEY } from '../../constants'
import { StepByStepSectionData } from '../../constructSectionsData'
import { ContentSectionWrapper, Container } from './styles'

type Props = {
  resume: Resume
  updateCard: UpdateCardFunc
  deleteCard: CardOperationFunc
  addCard: (cardId: string, sectionId: ResumeSectionId, options?: { shouldOpen: boolean }) => void
  addCustomSection: (customSectionId: string, options: AddCustomSectionPayload['options']) => void
  moveCard: MoveCardFunc
  moveSection: (payload: MoveSectionPayload) => void
  levels: GeneralEditorLevels
  updateSimpleField: UpdateSimpleFieldFunc
  renameSection: RenameSectionFunc
  deleteSection: DeleteSectionFunc
  appLocale?: string
  currentSection: Optional<StepByStepSectionData>
  handleChangeStep: (sectionId: StepByStepFlowStepId) => void
}

export const ContentSection = (props: Props) => {
  const {
    addCard,
    addCustomSection,
    deleteSection,
    renameSection,
    currentSection,
    handleChangeStep,
  } = props
  const { i18n } = useI18n()
  const { resume, moveCard, deleteCard, updateCard } = props
  const dispatch = useDispatch()
  const openedCard = useTypedSelector(editorSelectors.openedCard)
  const isATSTemplate = ATS_TEMPLATES.includes(resume.template as TemplateName)
  const sections = constructSectionsData(resume)
  const { isPhone } = useMediaQueries()
  const filteredSections = sections.filter(section =>
    displaySectionPerTemplateConfig(resume.template, section.id),
  )
  const visitedSections = JSON.parse(
    localStorage.getItem(`${STEP_BY_STEP_FLOW_VISITED_SECTIONS_LOCAL_STORAGE_KEY}_${resume.id}`) ||
      '[]',
  )

  const isCardOpen = useCallback(
    (cardId: Optional<number | string>, sectionId: ResumeSectionId) => {
      if (!openedCard) return false

      return openedCard.id === cardId && openedCard.sectionId === sectionId
    },
    [openedCard],
  )

  const handleCardToggle = useCallback(
    (cardId: Optional<number | string>, sectionId: ResumeSectionId) => {
      const isOpen = isCardOpen(cardId, sectionId)

      dispatch(editorActions.openCard(isOpen ? null : { id: cardId, sectionId }))
    },
    [dispatch, isCardOpen],
  )

  const handleCardAdd: AddCardFunc = useCallback(
    (sectionId, options) => {
      const cardId = generateRandomId()
      addCard(cardId, sectionId, { ...(options || {}), shouldOpen: true })
    },
    [addCard],
  )

  useEffect(() => {
    if (!currentSection || !visitedSections) return

    if (
      !visitedSections.includes(currentSection.id as ResumeSectionId) &&
      currentSection?.items?.length === 0 &&
      !isPhone
    ) {
      const cardId = generateRandomId()
      addCard(cardId, currentSection.id as ResumeSectionId, {
        shouldOpen: true,
      })

      localStorage.setItem(
        `${STEP_BY_STEP_FLOW_VISITED_SECTIONS_LOCAL_STORAGE_KEY}_${resume.id}`,
        JSON.stringify([...visitedSections, currentSection.id as ResumeSectionId]),
      )
    }
  }, [addCard, currentSection, dispatch, isPhone, resume.id, visitedSections])

  return (
    <Container>
      <ContentSectionWrapper>
        {currentSection &&
          (currentSection.id === SectionNames.details ? (
            isATSTemplate ? (
              <ATSPersonalDetailsSection resume={resume} isStepByStepFlow isFoldable={false} />
            ) : (
              <PersonalDetailsSection resume={resume} isStepByStepFlow isFoldable={false} />
            )
          ) : currentSection.id === SectionNames.powerStatement ? (
            <PowerStatementSection
              {...{
                sectionId: SectionNames.powerStatement,
                isStepByStepFlow: true,
                isFoldable: false,
              }}
            />
          ) : currentSection.id === SectionNames.profile ? (
            <ProfileSection
              {...{ sectionId: SectionNames.profile, isStepByStepFlow: true, isFoldable: false }}
            />
          ) : currentSection.id === SectionNames.addAdditionalSection ? (
            <AdditionalSections
              customTitle={i18n.t(
                'builder.resume_editor.step_by_step_editor.sections.add_additional_sections_title',
              )}
              customDescription={i18n.t(
                'builder.resume_editor.step_by_step_editor.sections.add_additional_sections_description',
              )}
              isStepByStepFlow
              sections={filteredSections}
              onAdd={sectionId => {
                handleChangeStep(sectionId)
                handleCardAdd(sectionId, { scrollIntoViewport: false, shouldOpen: true })
              }}
              onAddCustom={externalId => {
                handleChangeStep(`custom:${externalId}`)
                addCustomSection(externalId, { scrollIntoViewport: false })
              }}
            />
          ) : (
            <Section
              {...props}
              item={currentSection as SectionData}
              locale={resume.locale}
              isCardOpen={isCardOpen}
              onCardMove={moveCard}
              onCardDelete={deleteCard}
              onCardChange={updateCard}
              onCardToggle={handleCardToggle}
              onDelete={deleteSection}
              onRename={renameSection}
              onAdd={handleCardAdd}
              isOpened={true}
              isStepByStepFlow
              isFoldable={false}
            />
          ))}
      </ContentSectionWrapper>
    </Container>
  )
}
